import { Button } from "@studyplus/boron-ui";
import classNames from "classnames";
import { useCallback, useState } from "react";
import Loader from "../../atoms/Loader";
import { Modal } from "../../atoms/Modal";
import { ImagePreview } from "./ImagePreview";
import { PdfScrollViewer } from "./PdfScrollViewer";
import { isPdf } from "./useAttachmentPreview";

export { useAttachmentPreview } from "./useAttachmentPreview";

// NOTE: 文言は仕様書の未決事項「中身を表示できない旨の文言」。確定したら差し替える
export const UNVIEWABLE_MESSAGE =
  "中身を表示できません。内容を確かめたうえで添付してください";

type Props = {
  // null のときは閉じている
  file: File | null;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * 添付するファイルの中身を、添付を確定する前に見せるプレビュー画面
 * 開いている間はフォームの他の操作を受け付けない（モーダルで覆う）
 */
export const AttachmentPreviewModal = ({
  file,
  onConfirm,
  onCancel,
}: Props) => {
  return (
    <Modal
      isOpen={!!file}
      // 「キャンセル」「添付する」のどちらかを押すまで閉じない（Esc・背景のクリックでは閉じない）
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      // NOTE: 中のスクロール領域が残りの高さを埋めるよう、高さを決めておく。
      //       共通の枠線は黒帯の両端に出るので外す
      className="!h-[85vh] !max-h-[85vh] !w-[90%] !max-w-[800px] !border-0"
    >
      {file && (
        <PreviewContent
          key={`${file.name}-${file.size}-${file.lastModified}`}
          file={file}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      )}
    </Modal>
  );
};

type Status = "loading" | "ready" | "unviewable";

const PreviewContent = ({
  file,
  onConfirm,
  onCancel,
}: Props & { file: File }) => {
  const [status, setStatus] = useState<Status>("loading");
  const [numPages, setNumPages] = useState<number | null>(null);
  const handleLoad = useCallback(() => setStatus("ready"), []);
  const handleError = useCallback(() => setStatus("unviewable"), []);
  const pdf = isPdf(file);

  return (
    <>
      <div className="flex shrink-0 items-center gap-4 border-b border-gray-300 px-6 py-4">
        <span
          className={classNames(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded text-xs font-bold text-white",
            pdf ? "bg-red-400" : "bg-blue-400",
          )}
        >
          {pdf ? "PDF" : "IMG"}
        </span>
        <div className="min-w-0">
          <p className="m-0 break-all text-sm font-bold">{file.name}</p>
          <p className="m-0 text-xs text-gray-700">
            {pdf ? `PDF${numPages ? `・${numPages}ページ` : ""}` : "画像"}
          </p>
        </div>
      </div>
      <div className="relative flex min-h-0 flex-1 flex-col">
        {status === "unviewable" ? (
          <p role="alert" className="px-6 py-16 text-center">
            {UNVIEWABLE_MESSAGE}
          </p>
        ) : (
          <>
            <Loader loading={status === "loading"} />
            {pdf ? (
              <PdfScrollViewer
                className="flex-1"
                file={file}
                onOpen={setNumPages}
                onLoad={handleLoad}
                onError={handleError}
              />
            ) : (
              <div className="box-border min-h-0 flex-1 overflow-auto bg-gray-400 px-6 py-4">
                <ImagePreview
                  file={file}
                  onLoad={handleLoad}
                  onError={handleError}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex shrink-0 items-center justify-between gap-4 border-t border-gray-300 px-6 py-4">
        {/* NOTE: 中身を見てから添付してもらうための案内なので、ボタンに埋もれない大きさと濃さにする */}
        <p className="m-0 text-sm font-bold text-black">
          送信前に内容を確認してください
        </p>
        <div className="flex shrink-0 gap-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            キャンセル
          </Button>
          {/* 中身を見ずに添付できる隙間を作らないため、読み込み中は押せない */}
          <Button
            type="button"
            onClick={onConfirm}
            disabled={status === "loading"}
          >
            添付する
          </Button>
        </div>
      </div>
    </>
  );
};
